import {Dispatch, SetStateAction} from 'react'
import {ID, toAbsoluteUrl} from '../../../_metronic/helpers'
import axios from 'axios'
import {ISelectOption} from '../core/_model'
import {FormikContextType, FormikValues} from 'formik'
import {toast} from 'react-toastify'

export function formatDate(date: any) {
  if (!date) {
    return
  }
  const newDate = new Date(date)
  return newDate.toISOString().replace('T', ' ').slice(0, -8)
}

export function dateFormat(
  dateString: string,
  showTime: boolean = false,
  showSecs: boolean = false
): string {
  const date = new Date(dateString)

  const day = date.getDate()
  const year = date.getFullYear()

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const month = monthNames[date.getMonth()]

  let formattedDate = `${day} ${month} ${year}`

  if (showTime) {
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const secs = date.getSeconds()
    const ampm = hours >= 12 ? 'PM' : 'AM'

    hours = hours % 12 || 12 // Convert to 12-hour format

    const paddedMinutes = minutes.toString().padStart(2, '0')
    showSecs
      ? (formattedDate += `, ${hours}:${paddedMinutes}:${secs} ${ampm}`)
      : (formattedDate += `, ${hours}:${paddedMinutes} ${ampm}`)
  }

  return formattedDate
}

// User profile
export const userIdDefine = (id: number | undefined) => {
  let userid
  switch (id) {
    case 1:
      userid = 'Super Admin'
      break
    case 2:
      userid = 'Reseller'
      break
    case 3:
      userid = 'Client'
      break
    case 4:
      userid = 'Investor'
      break
    case 5:
      userid = 'Report Viewer'
      break
    case 6:
      userid = 'Team Member'
      break
  }
  return userid
}

export const brandDefine = (id: number) => {
  let brand
  if (id === 0) {
    brand = 'Non-Branded'
  } else if (id === 1) {
    brand = 'Branded'
  }
  return brand
}

export const userType = (type: number | undefined): string => {
  let typeName = ''
  if (type === 1) {
    typeName = 'Top Admin'
  } else if (type === 2) {
    typeName = 'Top Reseller'
  } else if (type === 3) {
    typeName = 'Client'
  } else {
    typeName = 'Unknown'
  }
  return typeName
}

export function calculatedGroupingIsDisabled<T>(
  isLoading: boolean,
  data: Array<T> | undefined
): boolean {
  return isLoading || !data || data.length === 0
}

export function calculateIsAllDataSelected(
  data: Array<any> | undefined,
  selected: Array<ID>
): boolean {
  return !!data && data.length > 0 && data.length === selected.length
}

export function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  const updatedSelected = selected.includes(id)
    ? selected.filter((itemId) => itemId !== id)
    : [...selected, id]

  setSelected(updatedSelected)
}

export function groupingOnSelectAll(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<{id?: ID}>
) {
  if (!data || data.length === 0) {
    return
  }

  const selectedIds = isAllSelected ? [] : (data.map((item) => item.id!).filter(Boolean) as ID[])
  setSelected(selectedIds)
}

export const searchItems = <T extends object>(
  list: T[],
  searchTerm: string,
  keys: (keyof T)[]
): T[] => {
  if (!searchTerm) return list

  return list.filter((item) =>
    keys.some((key) => String(item[key]).toLowerCase().includes(searchTerm.toLowerCase()))
  )
}

export const handleApiError = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response) {
    throw new Error(
      error.response.data.error_message || error.response.data.message || 'Something went wrong'
    )
  } else {
    throw new Error('Something went wrong')
  }
}

export const kFormatter = (num: number | undefined) => {
  if (!num) return 0
  if (Math.abs(num) > 999) {
    const formattedNum = (Math.abs(num) / 1000).toFixed(1)
    return Math.sign(num) * parseFloat(formattedNum) + 'K'
  } else {
    return num
  }
}

export const kFormatter2 = (num: number | undefined) => {
  if (!num) return 0

  if (Math.abs(num) > 999) {
    const raw = Math.abs(num) / 1000
    const floored = Math.floor(raw * 10) / 10 // floor to 1 decimal place
    return `${Math.sign(num) * floored}K`
  } else {
    return num
  }
}

export function getLastSixHoursFormatted(): string[] {
  const hoursArray: string[] = []
  const now = new Date()

  for (let i = 0; i < 6; i++) {
    const pastHour = new Date()
    pastHour.setHours(now.getHours() - i)

    // Format to HH:mm (24-hour format)
    const formattedTime = pastHour.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    hoursArray.push(formattedTime)
  }

  return hoursArray
}

export function getLastSixHoursRange(): string {
  const now = new Date()
  const past = new Date()

  // Subtract 6 hours from the current time
  past.setHours(now.getHours() - 6)

  // Format the time as HH:mm (24-hour format)
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  // Create the range string
  return `${formatTime(now)} - ${formatTime(past)}`
}

export type RangeObject = {
  dateStart: string
  dateEnd: string
  timeStart: string
  timeEnd: string
}

export function getDateTimeRangeById(timeRanges: ISelectOption[], id: number): RangeObject {
  let values = {
    dateStart: '',
    dateEnd: '',
    timeStart: '00:00',
    timeEnd: '00:00',
  }

  // Find the offset configuration by ID
  const config = timeRanges.find((item) => item.id === id)
  if (!config) {
    throw new Error(`No offset configuration found for ID: ${id}`)
  }

  const now = new Date()
  const past = new Date()

  // Format the date and time as YYYY-MM-DD HH:mm
  const formatDateTime = (date: Date) => {
    const formattedDate = date.toISOString().split('T')[0] // Get YYYY-MM-DD
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return {
      formattedDate,
      formattedTime,
    }
  }

  // Adjust the past date/time based on the offset and unit
  switch (id) {
    case 1: // Last 15 minutes
      past.setMinutes(now.getMinutes() - 15)
      break
    case 2: // Last 6 hours
      past.setHours(now.getHours() - 6)
      break
    case 3: // Last 24 hours
      past.setHours(now.getHours() - 24)
      break
    case 4: // Last 7 days
      past.setDate(now.getDate() - 7)
      break
    case 5: // Last 30 days
      past.setMonth(now.getMonth() - 1)
      break
    case 6: // This week
      const thisWeekRange = getWeekRange('current')
      values = {
        dateStart: thisWeekRange.startDate,
        dateEnd: thisWeekRange.endDate,
        timeStart: '00:00',
        timeEnd: '00:00',
      }
      return values
    case 7: // Last week
      const lastWeekRange = getWeekRange('previous')
      return {
        dateStart: lastWeekRange.startDate,
        dateEnd: lastWeekRange.endDate,
        timeStart: '00:00',
        timeEnd: '00:00',
      }
    case 8: // This month
      const currentMonth = getMonthRange('current')
      return (values = {
        dateStart: currentMonth.startDate,
        dateEnd: currentMonth.endDate,
        timeStart: '00:00',
        timeEnd: '00:00',
      })
    case 9: // Last month
      const previousMonth = getMonthRange('previous')
      return (values = {
        dateStart: previousMonth.startDate,
        dateEnd: previousMonth.endDate,
        timeStart: '00:00',
        timeEnd: '00:00',
      })
    case 10: // Previous N hours
      past.setMonth(now.getHours())
      break
    case 11: // Previous N days
      past.setMonth(now.getDate())
      break
    case 12: // Custom
      past.setMonth(now.getMonth())
      break
    case 13: // Today
      return (values = {
        dateStart: formatDateTime(now).formattedDate,
        dateEnd: formatDateTime(now).formattedDate,
        timeStart: '11:59',
        timeEnd: '12:00',
      })
      break
    case 14: // 1 Hour Ago
      past.setHours(now.getHours() - 1)
      break
    case 15: // 3 Hour Ago
      past.setHours(now.getHours() - 3)
      break
    case 16: // Last three days
      past.setDate(now.getDate() - 3)
      break
    case 17: // Yesterday
      // start of yesterday
      past.setDate(now.getDate())
      past.setHours(0, 0, 0, 0)

      // end of yesterday
      now.setDate(now.getDate() - 1)
      now.setHours(23, 59, 59, 999)
      break
    default:
      past.setMinutes(now.getMinutes() - 15)
      break
    // throw new Error('Invalid unit provided')
  }

  const startDate = formatDateTime(now)
  const endDate = formatDateTime(past)

  // console.log(startDate, endDate)

  values = {
    dateStart: endDate.formattedDate,
    dateEnd: startDate.formattedDate,
    timeStart: checkDateForGetTime(id, startDate.formattedTime),
    timeEnd: checkDateForGetTime(id, endDate.formattedTime),
  }

  // return {
  //   dateStart: startDate.formattedDate,
  //   dateEnd: endDate.formattedDate,
  //   timeStart: checkDateForGetTime(id, startDate.formattedTime),
  //   timeEnd: checkDateForGetTime(id, endDate.formattedTime),
  // }
  return values
}

function getMonthRange(type: 'current' | 'previous'): {startDate: string; endDate: string} {
  const now = new Date()

  if (type === 'current') {
    // First day of current month
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
    console.log(startDate)
    console.log(startDate.toLocaleDateString('en-CA').split('T')[0]) // Outputs in 'YYYY-MM-DD' format

    // Last day of current month
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    return {
      startDate: startDate.toLocaleDateString('en-CA').split('T')[0],
      endDate: endDate.toLocaleDateString('en-CA').split('T')[0],
    }
  } else if (type === 'previous') {
    // First day of previous month
    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)

    // Last day of previous month
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0)

    return {
      startDate: startDate.toLocaleDateString('en-CA').split('T')[0],
      endDate: endDate.toLocaleDateString('en-CA').split('T')[0],
    }
  }

  throw new Error('Invalid type for month range calculation.')
}

function getWeekRange(type: 'current' | 'previous'): {startDate: string; endDate: string} {
  const now = new Date()
  let startDate: Date
  let endDate: Date

  if (type === 'current') {
    // Start of this week (Monday)
    const diff = now.getDay() === 0 ? 6 : now.getDay() - 2
    startDate = new Date(now)
    startDate.setDate(now.getDate() - diff)
    startDate.setHours(0, 0, 0, 0)

    // End of this week (Sunday)
    endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + 6)
  } else if (type === 'previous') {
    // Start with current week's Monday
    const diff = now.getDay() === 0 ? 6 : now.getDay() - 2
    startDate = new Date(now)
    startDate.setDate(now.getDate() - diff)
    // Then go back 7 days to get to previous week's Monday
    startDate.setDate(startDate.getDate() - 7)
    startDate.setHours(0, 0, 0, 0)

    // End date is 6 days after start date
    endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + 6)
  } else {
    throw new Error('Invalid type for week range calculation.')
  }

  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  }
}

export const settingTimeRange = <T>(range: RangeObject, formik: FormikContextType<T>) => {
  formik.setFieldValue('exportTimeRangeStartDate', range.dateStart)
  formik.setFieldValue('exportTimeRangeEndDate', range.dateEnd)
  formik.setFieldValue('exportTimeRangeStartTime', range.timeEnd)
  formik.setFieldValue('exportTimeRangeEndTime', range.timeStart)
}

export const checkDateForGetTime = (id: number, time: string) => {
  let timing = '00:00'

  if (id === 1 || id === 2 || id === 3 || id === 14 || id === 15 || id === 17) {
    timing = time
    return timing
  }

  return timing
}

function getPreviousDates(type: 'week' | 'month'): {startDate: string; endDate: string} {
  const now = new Date()

  let startDate: Date
  let endDate: Date

  if (type === 'week') {
    // Calculate the end date of the previous week
    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 1)

    // Calculate the start date of the previous week
    startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 6)
  } else if (type === 'month') {
    // Calculate the end date of the previous month (last day of the previous month)
    endDate = new Date(now.getFullYear(), now.getMonth(), 0)

    // Calculate the start date of the previous month (first day of the previous month)
    startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
  } else {
    throw new Error("Invalid type provided. Must be 'week' or 'month'.")
  }

  // Format the dates as YYYY-MM-DD
  const formatDate = (date: Date): string => date.toISOString().split('T')[0]

  console.log({startDate: formatDate(startDate), endDate: formatDate(endDate)})

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  }
}

export function formatDateAsDayAndTime(dateString: string): string {
  const date = new Date(dateString)

  if (isNaN(date.getTime())) {
    // throw new Error('Invalid date string')
    return ''
  }

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24-hour format, set to true for AM/PM
  }

  const formattedDate = date.toLocaleString('en-GB', options) // 'en-GB' ensures day-month order
  const [dayMonth, time] = formattedDate.split(', ')

  return dayMonth
}

export function handleError(error: unknown) {
  let errorMessage = 'An unknown error occurred'

  if (axios.isAxiosError(error)) {
    errorMessage = error.response?.data?.message || `Request failed: ${error.message}`
  } else if (error instanceof Error) {
    errorMessage = error.message
  }

  return errorMessage
}

export const handleAxiosError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      console.error('Response error:', error.response.data)
      return (
        error.response.data?.message ||
        `Error: ${error.response.status} - ${
          error.response.data.messages ? error.response.data.messages : ''
        }`
      )
    } else if (error.request) {
      console.error('No response received:', error.request)
      return 'No response from server. Please try again.'
    } else {
      console.error('Axios error:', error.message)
      return 'An unexpected Axios error occurred.'
    }
  } else if (error instanceof Error) {
    // Handle regular JS/TS errors
    console.error('Standard error:', error.message)
    return error.message
  } else {
    console.error('Unknown error:', error)
    return 'An unknown error occurred.'
  }
}

export const base64ToFile = (base64: string, filename: string): File => {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, {type: mime})
}

export const base64ToFileObj = (base64: string, filename: string): File => {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  let u8arr: Uint8Array

  if (typeof window === 'undefined') {
    // Node.js environment
    const buffer = Buffer.from(arr[1], 'base64')
    u8arr = new Uint8Array(buffer)
  } else {
    // Browser environment
    const bstr = atob(arr[1])
    u8arr = new Uint8Array(bstr.length)
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i)
    }
  }

  return new File([u8arr], filename, {type: mime})
}

export const isBase64Image = (str: string): boolean => {
  const base64ImageRegex =
    /^data:image\/(png|jpeg|jpg|gif|bmp|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/
  return base64ImageRegex.test(str)
}

export const setAvatar = (photo: string, mode: string): string => {
  const avatar =
    mode === 'dark' ? '/media/svg/avatars/blank-dark.svg' : '/media/svg/avatars/blank.svg'

  const photo_src = photo && photo !== '' && isBase64Image(photo) ? photo : toAbsoluteUrl(avatar)

  return photo_src
}

export const setAvatarDefault = (photo: string): string => {
  const photo_src =
    photo && photo !== '' && isBase64Image(photo)
      ? photo
      : toAbsoluteUrl('/media/avatars/blank.png')

  // console.log(photo_src)

  return photo_src
}

export const convertToBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const getMimeTypeFromBase64 = (base64: string): string => {
  const match = base64.match(/^data:(.*?);base64,/)
  return match ? match[1] : 'application/octet-stream'
}

export function downloadFile(file: File, filename?: string) {
  const url = URL.createObjectURL(file)

  const link = document.createElement('a')
  link.href = url
  link.download = filename || file.name // fallback to file's original name
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// CHECK IS REDDATA
export const isReddata = (): boolean => {
  const hostname = window.location.hostname
  return hostname === 'redsmsc.iatlbd.com' || hostname === 'sms.reddata.com.bd' ? true : false
}

// N DAYS AGO
export const nDaysAgo = (n: number, startrange?: string): Date => {
  const d = startrange ? new Date(startrange) : new Date()
  d.setDate(d.getDate() - Math.abs(n))
  return d
}
